import type { GridGalleryProps } from "@site-types/components/workGridGallery"
import { useRouteShellContext } from "@components/routeShellContext"
import useEmblaCarousel from "embla-carousel-react"
import { useCallback, useEffect, useState } from "react"
import { imageAlt, imageRenderProps, imageTitle } from "../../componentUtils"
import OptimizedImage from "../../OptimizedImage"
import "./styles.css"

export function GridGallery({ products }: GridGalleryProps) {
  const { locale } = useRouteShellContext()
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)
  const [viewportRef, emblaApi] = useEmblaCarousel({
    align: "start",
    breakpoints: {
      "(min-width: 600px)": { active: false },
    },
    containScroll: "trimSnaps",
    dragFree: false,
    loop: false,
  })

  const updateCarouselState = useCallback(() => {
    if (!emblaApi) return

    setSelectedIndex(emblaApi.selectedScrollSnap())
    setCanScrollPrev(emblaApi.canScrollPrev())
    setCanScrollNext(emblaApi.canScrollNext())
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return

    updateCarouselState()
    emblaApi.on("select", updateCarouselState)
    emblaApi.on("settle", updateCarouselState)
    emblaApi.on("reInit", updateCarouselState)

    return () => {
      emblaApi.off("select", updateCarouselState)
      emblaApi.off("settle", updateCarouselState)
      emblaApi.off("reInit", updateCarouselState)
    }
  }, [emblaApi, updateCarouselState])

  const scrollPrev = () => {
    emblaApi?.scrollPrev()
  }

  const scrollNext = () => {
    emblaApi?.scrollNext()
  }

  const scrollToProduct = (index: number) => {
    emblaApi?.scrollTo(index)
  }

  return (
    <div
      className="section-gallery grid-gallery"
      data-id="grid-gallery"
      id="grid-gallery"
    >
      <div
        className="grid-gallery__viewport"
        ref={viewportRef}
      >
        <ul className="grid-gallery__container">
          {products.map((entry, index) => (
            <li
              className="grid-gallery__slide"
              data-id={`grid-gallery-item-${entry.id ?? index}`}
              key={entry.id ?? index}
            >
              <a
                className="grid-gallery__link"
                data-id={`grid-gallery-link-${entry.id ?? index}`}
                href={entry.url ?? "#"}
                target="_blank"
                rel="noreferrer"
                onFocus={() => scrollToProduct(index)}
              >
                {entry.coverImage ? (
                  <OptimizedImage
                    className="grid-gallery__image"
                    title={imageTitle(entry.coverImage, locale)}
                    alt={imageAlt(entry.coverImage, locale)}
                    {...imageRenderProps(entry.coverImage, "workCover")}
                  />
                ) : (
                  <span>{entry.title}</span>
                )}
              </a>
            </li>
          ))}
        </ul>
      </div>
      <div
        className="grid-gallery__controls"
        aria-label="Product gallery controls"
      >
        <button
          className="grid-gallery__control grid-gallery__control--prev"
          data-id="grid-gallery-prev"
          type="button"
          onClick={scrollPrev}
          disabled={!canScrollPrev}
          aria-label="Previous product"
        >
          <i className="fa-sharp fa-solid fa-chevron-left" />
        </button>
        <div
          className="grid-gallery__status"
          data-id="product-gallery-status"
          data-testid="product-gallery-status"
          aria-live="polite"
        >
          {products.length > 0 ? selectedIndex + 1 : 0} / {products.length}
        </div>
        <button
          className="grid-gallery__control grid-gallery__control--next"
          data-id="grid-gallery-next"
          type="button"
          onClick={scrollNext}
          disabled={!canScrollNext}
          aria-label="Next product"
        >
          <i className="fa-sharp fa-solid fa-chevron-right" />
        </button>
      </div>
    </div>
  )
}

export default GridGallery
